import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Grid,
  Typography,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { TextField as TextF, MenuItem } from '@material-ui/core';
import { Button as Submit } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import useReactRouter from 'use-react-router';
import HeaderMenu from '../../Components/Header/headeradmission';
import CivilConferenceMenu from '../../Components/Header/civilconferencemenu';

const useStyles = makeStyles((theme) => ({
  page: {
    background: '#fff',
    boxShadow: 'rgb(216 216 216) 0px 0px 15px 0px',
    padding: '15px',
  },
  formWrapper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  sectionTitle: {
    color: '#c0392b',
    fontWeight: 'bold',
    fontSize: '15px',
    marginBottom: 4,
  },
  sectionDivider: {
    marginBottom: theme.spacing(1.5),
  },
  labelText: {
    fontSize: '13px',
    color: '#444',
    marginBottom: 3,
    display: 'block',
    fontWeight: 500,
  },
  feeBox: {
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: '6px 10px',
    background: '#f9f9f9',
    fontSize: 14,
    minHeight: 38,
    display: 'flex',
    alignItems: 'center',
  },
  noteText: {
    color: '#666',
    fontSize: '12px',
    fontStyle: 'italic',
    marginLeft: 8,
  },
  submitBtn: {
    backgroundColor: '#009688',
    color: '#fff',
    padding: '8px 36px',
    fontSize: 14,
    '&:hover': { backgroundColor: '#00796b' },
  },
  successContainer: {
    textAlign: 'center',
    padding: theme.spacing(6),
  },
  fileLabel: {
    fontSize: 13,
    color: '#444',
    fontWeight: 500,
    display: 'block',
    marginBottom: 4,
  },
}));

const submissionTypeOptions = [
  { value: 'Author', label: 'Author' },
  { value: 'Participant Only', label: 'Participant Only' },
];

function readFile(e, setContent, setName, setSave, maxKB = 2000) {
  const file = e.target.files[0];
  if (!file) return;
  if (file.size > maxKB * 1000) {
    alert(`File size should be less than ${maxKB}KB`);
    e.target.value = '';
    return;
  }
  const reader = new FileReader();
  setSave(true);
  reader.onloadend = () => {
    setName(file.name);
    setContent(reader.result.split(',')[1]);
  };
  reader.readAsDataURL(file);
}

const ConferenceCivilEngineering = () => {
  const classes = useStyles();
  const { match } = useReactRouter();
  const instituteId = match?.params?.id || '';

  // Pre-filled data from login (if existing applicant)
  const prefill = (() => {
    try { return JSON.parse(sessionStorage.getItem('civilConferenceInfo')) || {}; }
    catch { return {}; }
  })();

  const [instInfo, setInstInfo] = useState({});
  const [classData, setClassData] = useState([]);
  const [classDataId, setClassDataId] = useState('');
  const [groupData, setGroupData] = useState([]);
  const [groupDataId, setGroupDataId] = useState('');
  const [groupInfo, setGroupInfo] = useState({});
  const [submissionType, setSubmissionType] = useState(prefill?.submissionType || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);

  // Photo
  const [photoContent, setPhotoContent] = useState('');
  const [photoName, setPhotoName] = useState('');
  const [photoSave, setPhotoSave] = useState(false);

  // Supporting docs
  const [localContent, setLocalContent] = useState('');
  const [localName, setLocalName] = useState('');
  const [localSave, setLocalSave] = useState(false);

  const [intlContent, setIntlContent] = useState('');
  const [intlName, setIntlName] = useState('');
  const [intlSave, setIntlSave] = useState(false);

  const [saarcContent, setSaarcContent] = useState('');
  const [saarcName, setSaarcName] = useState('');
  const [saarcSave, setSaarcSave] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_ROOT}/public/online-applicantion/institute-info/${instituteId}`
        );
        const data = await res.json();
        setInstInfo(data?.item || {});

        const classRes = await fetch(
          `${process.env.REACT_APP_API_ROOT}/public/online/admission/class/list?instituteId=${instituteId}`
        );
        const classJson = await classRes.json();
        setClassData(classJson?.item || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  const handleClassChange = async (e) => {
    const id = e.target.value;
    setClassDataId(id);
    setGroupDataId('');
    setGroupInfo({});
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_ROOT}/public/online/admission/group/configuration/list/by/class-id?classId=${id}&instituteId=${instituteId}`
      );
      const json = await res.json();
      setGroupData(json?.item || []);
    } catch (err) { console.error(err); }
  };

  const handleGroupChange = (e) => {
    const id = e.target.value;
    setGroupDataId(id);
    const found = groupData.find((item) => item.id == id);
    setGroupInfo(found || {});
  };

  const INITIAL_FORM_STATE = {
    applicantName: prefill?.applicantName || '',
    localGuardianEmail: prefill?.localGuardianEmail || '',
    mobileNumber: prefill?.mobileNumber || '',
    paperId: prefill?.paperId || '',
    instituteOrganization: prefill?.instituteOrganization || prefill?.designationAndOrganization || '',
    city: prefill?.city || '',
    zipPostalCode: prefill?.zipPostalCode || '',
    country: prefill?.country || '',
  };

  const FORM_VALIDATION = Yup.object().shape({
    applicantName: Yup.string().required('Full Name is required'),
    localGuardianEmail: Yup.string().email('Invalid email').required('Email is required'),
    mobileNumber: Yup.string().required('Contact No. is required'),
    instituteOrganization: Yup.string().required('Institution/Organization is required'),
    city: Yup.string().required('City is required'),
    zipPostalCode: Yup.string().required('Zip/Postal Code is required'),
    country: Yup.string().required('Country is required'),
  });

  const handleSubmit = async (values) => {
    if (!classDataId) { alert('Please select a Class'); return; }
    if (!groupDataId) { alert('Please select a Group'); return; }
    if (!submissionType) { alert('Please select a Submission Type'); return; }

    const postData = {
      applicantName: values.applicantName,
      applicantNameBangla: '',
      localGuardianEmail: values.localGuardianEmail,
      mobileNumber: values.mobileNumber,
      paperId: values.paperId,
      submissionType: submissionType,
      instituteOrganization: values.instituteOrganization,
      city: values.city,
      zipPostalCode: values.zipPostalCode,
      country: values.country,
      designationAndOrganization: values.instituteOrganization,
      officeAddressAndContractNo: '',
      instituteId: parseInt(instituteId),
      groupConfigId: groupInfo?.id,
      admissionCharge: groupInfo?.admissionCharge || 0,
      serviceCharge: groupInfo?.serviceCharge || 0,
      totalCharge: groupInfo?.totalCharge || 0,
      academicYear: new Date().getFullYear(),
      // Student photo
      studentImageFileContent: photoContent || 'R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
      studentImageName: photoName || 'no_image.png',
      studentImageFileSave: photoSave || true,
      // Supporting documents
      localStudentFileContent: localContent || '',
      localStudentFileName: localName || '',
      localStudentFileSave: localSave,
      internationalStudentFileContent: intlContent || '',
      internationalStudentFileName: intlName || '',
      internationalStudentFileSave: intlSave,
      saarcDeligateFileContent: saarcContent || '',
      saarcDeligateFileName: saarcName || '',
      saarcDeligateFileSave: saarcSave,
      // Defaults for unused required fields
      gender: '', religion: '', bloodGroup: '', nationality: '',
      dateOfBirth: '', birthcertificateNo: '',
      fatherName: '', fatherNameBangla: '', fatherMobile: '', fatherNid: '',
      fatherOccupation: '', fatherEducation: '', fatherDesignation: '', fatherIncome: '', fatherWorkPlace: '',
      fatherImageFileContent: '', fatherImageName: '', fatherImageFileSave: false,
      motherName: '', motherNameBangla: '', motherMobile: '', motherNid: '',
      motherOccupation: '', motherEducation: '', motherDesignation: '', motherIncome: '', motherWorkPlace: '',
      motherImageFileContent: '', motherImageName: '', motherImageFileSave: false,
      presentAddress: '', permanentAddress: '', presentThanaId: 0, permanentThanaId: 0,
      localGuardianName: '', localGuardianMobile: '',
      quota: '', roll: '', lastInstituteName: '', medium: '', autism: 0,
      passportNumber: '', mobileNumberWhatsapp: '', lastDegree: '',
      currentlyEmployeed: '', governmentOfficial: '',
      dateofIssuePassport: '', dateofExpairyPassport: '',
    };

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_ROOT}/public/online/admission/applicant-info/save`,
        {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(postData),
        }
      );
      const content = await res.json();
      setLoading(false);
      if (res.ok && content.messageType === 1) {
        setRegistrationId(content.item);
        setSuccess(true);
        sessionStorage.removeItem('civilConferenceInfo');
      } else {
        alert(content.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const downloadForm = () => {
    window.open(
      `${process.env.REACT_APP_API_ROOT}/public/online/admission/icce-confirmation/letter/download?instituteId=${instituteId}&registrationId=${registrationId}`,
      '_blank'
    );
  };

  // Download for existing applicant (pre-filled from login)
  const downloadExisting = () => {
    window.open(
      `${process.env.REACT_APP_API_ROOT}/public/online/admission/icce-confirmation/letter/download?instituteId=${instituteId}&registrationId=${prefill?.registrationId}`,
      '_blank'
    );
  };

  // Payment for existing applicant
  const paymentPageExisting = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_ROOT}/public/get/online-admission-fees-payment/spg/session-id?registrationId=${prefill?.registrationId}&instituteId=${parseInt(instituteId)}`,
        { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
      );
      const content = await res.json();
      if (content?.messageType === 1) {
        window.open(content?.item);
      } else {
        alert(content?.message);
      }
    } catch (err) { console.error(err); }
  };

  // Payment after new registration
  const paymentPage = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_ROOT}/public/get/online-admission-fees-payment/spg/session-id?registrationId=${registrationId}&instituteId=${parseInt(instituteId)}`,
        { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
      );
      const content = await res.json();
      if (content?.messageType === 1) {
        window.open(content?.item);
      } else {
        alert(content?.message);
      }
    } catch (err) { console.error(err); }
  };

  // Helper: small text field
  const Field = ({ label, name, values, handleChange, handleBlur, errors, touched, required, disabled, type = 'text', sm = 4 }) => (
    <Grid item xs={12} sm={sm}>
      <label className={classes.labelText}>{label}{required ? ' *' : ''}</label>
      <TextF
        name={name}
        variant="outlined"
        fullWidth
        size="small"
        type={type}
        disabled={disabled}
        value={values[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched[name] && Boolean(errors[name])}
        helperText={touched[name] && errors[name]}
      />
    </Grid>
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <HeaderMenu
          details={{
            instituteName: instInfo?.institutename,
            address: instInfo?.instituteaddress,
            image: instInfo?.imageLink,
          }}
        />
      </Grid>

      <Grid item xs={12} className={classes.page}>
        <Divider />
        <CivilConferenceMenu />

        {/* Page Title */}
        <Typography
          style={{
            fontSize: 18,
            textAlign: 'center',
            margin: '14px 0 10px',
            fontWeight: 600,
            borderBottom: '1px solid #ddd',
            paddingBottom: 12,
            letterSpacing: 0.3,
          }}
        >
          Conference Registration For Civil Engineering
        </Typography>

        {/* Download banner for returning applicants */}
        {prefill?.registrationId && !success && (
          <div
            style={{
              background: '#e0f2f1',
              border: '1px solid #009688',
              borderRadius: 6,
              padding: '12px 20px',
              margin: '0 0 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <div>
              <Typography style={{ fontWeight: 600, color: '#00695c', fontSize: 14 }}>
                Viewing existing application
              </Typography>
              <Typography style={{ color: '#004d40', fontSize: 13 }}>
                Registration ID: <strong>{prefill.registrationId}</strong>
                {prefill?.paymentStatus && (
                  <span
                    style={{
                      marginLeft: 12,
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 600,
                      background: prefill.paymentStatus === 'Paid' ? '#c8e6c9' : '#ffccbc',
                      color: prefill.paymentStatus === 'Paid' ? '#2e7d32' : '#bf360c',
                    }}
                  >
                    {prefill.paymentStatus}
                  </span>
                )}
              </Typography>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Submit
                variant="outlined"
                onClick={downloadExisting}
                style={{ borderColor: '#009688', color: '#009688', fontSize: 13, padding: '6px 18px', fontWeight: 600 }}
              >
                ⬇ Download
              </Submit>
              <Submit
                variant="contained"
                onClick={paymentPageExisting}
                style={{ backgroundColor: '#009688', color: '#fff', fontSize: 13, padding: '6px 18px', fontWeight: 600 }}
              >
                💳 Make Payment
              </Submit>
            </div>
          </div>
        )}

        {!success ? (
          <div className={classes.formWrapper}>
            <Container maxWidth="md">
              <Formik
                initialValues={INITIAL_FORM_STATE}
                validationSchema={FORM_VALIDATION}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, errors, touched, handleChange, handleBlur }) => (
                  <Form>
                    <Grid container spacing={2}>

                      {/* ===== A. Applicant Information ===== */}
                      <Grid item xs={12}>
                        <Typography className={classes.sectionTitle}>A. Applicant Information</Typography>
                        <Divider className={classes.sectionDivider} />
                      </Grid>

                      {/* Row 1: Class + Fees */}
                      <Grid item xs={12} sm={4}>
                        <label className={classes.labelText}>Program</label>
                        <TextF
                          select
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={classDataId}
                          onChange={handleClassChange}
                        >
                          {classData.map((item, i) => (
                            <MenuItem key={i} value={item.id}>{item.name}</MenuItem>
                          ))}
                        </TextF>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <label className={classes.labelText}>Application Fees</label>
                        <div className={classes.feeBox}>{groupInfo?.admissionCharge ?? ''}</div>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <label className={classes.labelText}>Service Charge</label>
                        <div className={classes.feeBox}>{groupInfo?.serviceCharge ?? ''}</div>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <label className={classes.labelText}>Total Charge</label>
                        <div className={classes.feeBox}>{groupInfo?.totalCharge ?? ''}</div>
                      </Grid>

                      {/* Row 2: Group */}
                      <Grid item xs={12} sm={4}>
                        <label className={classes.labelText}>Applicant Category</label>
                        <TextF
                          select
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={groupDataId}
                          onChange={handleGroupChange}
                        >
                          {groupData.map((item, i) => (
                            <MenuItem key={i} value={item.id}>{item.groupName}</MenuItem>
                          ))}
                        </TextF>
                      </Grid>

                      {/* Submission Type */}
                      <Grid item xs={12} sm={8}>
                        <label className={classes.labelText}>
                          Submission Type
                          <span className={classes.noteText}>
                            Note: If Submission Type is "Author", Paper ID is required; otherwise not required.
                          </span>
                        </label>
                        <TextF
                          select
                          variant="outlined"
                          size="small"
                          style={{ minWidth: 200 }}
                          label=""
                          value={submissionType}
                          onChange={(e) => setSubmissionType(e.target.value)}
                        >
                          {submissionTypeOptions.map((item, i) => (
                            <MenuItem key={i} value={item.value}>{item.label}</MenuItem>
                          ))}
                        </TextF>
                      </Grid>

                      {/* Paper ID */}
                      <Field
                        label="Paper ID"
                        name="paperId"
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        disabled={submissionType !== 'Author'}
                        sm={4}
                      />

                      {/* Full Name */}
                      <Field
                        label="Full Name" name="applicantName" required
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        sm={4}
                      />

                      {/* Email */}
                      <Field
                        label="Email" name="localGuardianEmail" required
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        sm={4}
                      />

                      {/* Contact No */}
                      <Field
                        label="Contact No." name="mobileNumber" required
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        sm={4}
                      />

                      {/* Applicant Photo */}
                      <Grid item xs={12} sm={4}>
                        <label className={classes.fileLabel}>Applicant Photo</label>
                        <input
                          type="file"
                          accept="image/*"
                          style={{
                            width: '100%',
                            padding: '7px 10px',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            fontSize: 13,
                            boxSizing: 'border-box',
                          }}
                          onChange={(e) => readFile(e, setPhotoContent, setPhotoName, setPhotoSave, 600)}
                        />
                        <Typography style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Max 600KB</Typography>
                      </Grid>

                      {/* ===== B. Affiliation ===== */}
                      <Grid item xs={12}>
                        <Typography className={classes.sectionTitle} style={{ marginTop: 8 }}>
                          B. Affiliation
                        </Typography>
                        <Divider className={classes.sectionDivider} />
                      </Grid>

                      <Field
                        label="Institution / Organization" name="instituteOrganization" required
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        sm={6}
                      />
                      <Field
                        label="City" name="city" required
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        sm={6}
                      />
                      <Field
                        label="Zip / Postal Code" name="zipPostalCode" required
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        sm={6}
                      />
                      <Field
                        label="Country" name="country" required
                        values={values} handleChange={handleChange}
                        handleBlur={handleBlur} errors={errors} touched={touched}
                        sm={6}
                      />

                      {/* ===== C. Supporting Documents ===== */}
                      <Grid item xs={12}>
                        <Typography className={classes.sectionTitle} style={{ marginTop: 8 }}>
                          C. Supporting Documents
                        </Typography>
                        <Divider className={classes.sectionDivider} />
                      </Grid>

                      {[
                        {
                          label: 'Local Students',
                          setContent: setLocalContent, setName: setLocalName, setSave: setLocalSave,
                        },
                        {
                          label: 'International Students',
                          setContent: setIntlContent, setName: setIntlName, setSave: setIntlSave,
                        },
                        {
                          label: 'Saarc Deligate',
                          setContent: setSaarcContent, setName: setSaarcName, setSave: setSaarcSave,
                        },
                      ].map(({ label, setContent, setName, setSave }, i) => (
                        <Grid item xs={12} key={i} style={{ marginBottom: 4 }}>
                          <label className={classes.fileLabel}>{label}</label>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            style={{
                              width: '100%',
                              padding: '7px 10px',
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              fontSize: 13,
                              boxSizing: 'border-box',
                            }}
                            onChange={(e) => readFile(e, setContent, setName, setSave, 2000)}
                          />
                        </Grid>
                      ))}

                      {/* Submit */}
                      <Grid item xs={12} style={{ textAlign: 'center', marginTop: 20 }}>
                        {loading ? (
                          <CircularProgress size={28} style={{ color: '#009688' }} />
                        ) : (
                          <Submit type="submit" variant="contained" className={classes.submitBtn}>
                            Save
                          </Submit>
                        )}
                      </Grid>

                    </Grid>
                  </Form>
                )}
              </Formik>
            </Container>
          </div>
        ) : (
          /* ===== Success Screen ===== */
          <div style={{
            maxWidth: 520,
            margin: '40px auto',
            padding: '32px 36px',
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
            <Typography variant="h5" style={{ color: '#f68b1f', fontWeight: 700, marginBottom: 8 }}>
              Registration Submitted!
            </Typography>
            <Typography variant="body1" style={{ color: '#555', marginBottom: 20 }}>
              Your Registration ID is{' '}
              <strong style={{ color: '#009688', fontSize: 18 }}>{registrationId}</strong>.
              <br />
              Please save this ID — you will need it to make payment and download your confirmation letter.
            </Typography>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Submit
                variant="outlined"
                style={{ borderColor: '#009688', color: '#009688', padding: '8px 24px', fontWeight: 600 }}
                onClick={downloadForm}
              >
                ⬇ Download Form
              </Submit>

              <Submit
                variant="contained"
                style={{ backgroundColor: '#009688', color: '#fff', padding: '8px 24px', fontWeight: 600 }}
                onClick={paymentPage}
              >
                💳 Make Payment
              </Submit>
            </div>
          </div>
        )}
      </Grid>
    </Grid>
  );
};

export default ConferenceCivilEngineering;
